import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Timer, Plus, Trash2 } from "lucide-react";
import { initials } from "@/lib/meta";
import { toast } from "sonner";

const fmt = (h) => {
  if (h == null) return "—";
  const n = Number(h) || 0;
  if (n === 0) return "0h";
  const hr = Math.floor(n);
  const min = Math.round((n - hr) * 60);
  return min ? `${hr}h ${min}m` : `${hr}h`;
};

export default function TimeTracking({ issue, onIssuePatched }) {
  const { users } = useApp();
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ hours: "", description: "", logged_at: new Date().toISOString().slice(0, 10) });
  const [est, setEst] = useState(issue.original_estimate ?? "");
  const [rem, setRem] = useState(issue.remaining_estimate ?? "");

  useEffect(() => {
    setEst(issue.original_estimate ?? "");
    setRem(issue.remaining_estimate ?? "");
  }, [issue.id, issue.original_estimate, issue.remaining_estimate]);

  const load = async () => setLogs((await api.get(`/issues/${issue.id}/worklogs`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [issue.id]);

  const save = async () => {
    if (!form.hours || Number(form.hours) <= 0) { toast.error("Enter hours > 0"); return; }
    await api.post(`/issues/${issue.id}/worklogs`, {
      hours: Number(form.hours),
      description: form.description,
      logged_at: form.logged_at ? new Date(form.logged_at).toISOString() : null,
    });
    toast.success(`Logged ${form.hours}h`);
    setOpen(false);
    setForm({ hours: "", description: "", logged_at: new Date().toISOString().slice(0, 10) });
    load();
    onIssuePatched?.();
  };

  const del = async (id) => {
    if (!window.confirm("Delete work log?")) return;
    await api.delete(`/worklogs/${id}`);
    load();
    onIssuePatched?.();
  };

  const patchEstimate = async (field, value) => {
    const v = value === "" ? null : Number(value);
    await api.patch(`/issues/${issue.id}`, { [field]: v });
    onIssuePatched?.();
  };

  const spent = Number(issue.time_spent || 0);
  const originalEst = Number(issue.original_estimate || 0);
  const pctSpent = originalEst > 0 ? Math.min(100, Math.round((spent / originalEst) * 100)) : 0;
  const over = originalEst > 0 && spent > originalEst;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Timer className="w-3.5 h-3.5" /> Time tracking
        </div>
        <button onClick={() => setOpen(true)} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="log-work-btn">
          <Plus className="w-3 h-3" /> Log work
        </button>
      </div>
      <div className="border border-border rounded-md p-3 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Estimated</div>
            <Input type="number" min="0" step="0.5" value={est} onChange={(e) => setEst(e.target.value)}
              onBlur={() => patchEstimate("original_estimate", est)} className="h-7 text-xs font-mono" data-testid="tt-estimate" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Remaining</div>
            <Input type="number" min="0" step="0.5" value={rem} onChange={(e) => setRem(e.target.value)}
              onBlur={() => patchEstimate("remaining_estimate", rem)} className="h-7 text-xs font-mono" data-testid="tt-remaining" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Spent</div>
            <div className="h-7 flex items-center px-2 text-xs font-mono">{fmt(spent)}</div>
          </div>
        </div>
        {originalEst > 0 ? (
          <div>
            <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground mb-1">
              <span>{fmt(spent)} of {fmt(originalEst)}</span>
              <span className={over ? "text-red-500" : ""}>{pctSpent}%{over ? " (over)" : ""}</span>
            </div>
            <Progress value={pctSpent} className="h-1.5" />
          </div>
        ) : null}
      </div>
      {logs.length > 0 ? (
        <div className="mt-2 space-y-1">
          {logs.slice(0, 6).map((w) => {
            const u = users.find((x) => x.id === w.user_id);
            return (
              <div key={w.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-accent group" data-testid={`worklog-${w.id}`}>
                {u ? <Avatar className="w-5 h-5"><AvatarImage src={u.avatar_url} /><AvatarFallback className="text-[8px]">{initials(u.name)}</AvatarFallback></Avatar>
                  : <div className="w-5 h-5 rounded-full bg-secondary" />}
                <span className="font-medium">{u ? u.name : "Someone"}</span>
                <span className="font-mono text-muted-foreground">{fmt(w.hours)}</span>
                <span className="text-muted-foreground truncate flex-1">{w.description || "—"}</span>
                <span className="text-[10px] text-muted-foreground font-mono">{new Date(w.logged_at).toLocaleDateString()}</span>
                {w.user_id === user.id ? (
                  <button onClick={() => del(w.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`del-worklog-${w.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="log-work-dialog">
          <DialogHeader><DialogTitle className="font-display">Log work</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hours</Label><Input type="number" min="0" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="1.5" data-testid="lw-hours" autoFocus /></div>
              <div><Label>Date</Label><Input type="date" value={form.logged_at} onChange={(e) => setForm({ ...form, logged_at: e.target.value })} data-testid="lw-date" /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What did you work on?" data-testid="lw-description" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="lw-submit">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
